import { NavLink } from "react-router-dom";
import {
  Bell,
  Database,
  Plug,
  Settings,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { NavTooltip } from "../components/NavTooltip";
import { TOPICS, TOP_RAIL_TOPIC_IDS } from "../registry/topics";
import type { TopicId } from "../types/dashboard";

type BottomItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
  showLabel?: boolean;
  active?: boolean;
};

const navBtnBase =
  "flex h-10 w-10 items-center justify-center rounded-lg transition-colors";

function navLinkClass(isActive: boolean, showLabel?: boolean) {
  return [
    showLabel
      ? "flex h-auto w-10 flex-col items-center justify-center gap-0.5 rounded-lg py-1.5 transition-colors"
      : navBtnBase,
    isActive
      ? "bg-accent/10 text-accent"
      : "text-nav-icon hover:bg-page hover:text-text-primary",
  ].join(" ");
}

function navBtnClass(active?: boolean, showLabel?: boolean) {
  return [
    showLabel
      ? "flex h-auto w-10 flex-col items-center justify-center gap-0.5 rounded-lg py-1.5 transition-colors"
      : navBtnBase,
    active
      ? "bg-accent/10 text-accent"
      : "text-nav-icon hover:bg-page hover:text-text-primary",
  ].join(" ");
}

const ADMIN_ONLY_TOPICS = new Set<TopicId>(["admin", "devops"]);
const TOP_RAIL_IDS = new Set<TopicId>(TOP_RAIL_TOPIC_IDS);

export function NavRail({
  onMenuClick,
  sidebarOpen,
  onUsersClick,
  usersOpen,
  onSettingsClick,
  settingsOpen,
  onLiveDataClick,
  liveDataOpen,
  showAdmin,
}: {
  onMenuClick?: () => void;
  sidebarOpen?: boolean;
  onUsersClick?: () => void;
  usersOpen?: boolean;
  onSettingsClick?: () => void;
  settingsOpen?: boolean;
  onLiveDataClick?: () => void;
  liveDataOpen?: boolean;
  showAdmin?: boolean;
}) {
  const visibleTopics = TOPICS.filter(
    (t) =>
      TOP_RAIL_IDS.has(t.id) && (!ADMIN_ONLY_TOPICS.has(t.id) || showAdmin),
  );

  const bottomItems: BottomItem[] = [
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      onClick: onSettingsClick,
      active: settingsOpen,
    },
    {
      id: "live-data",
      label: "Live data",
      icon: Database,
      onClick: onLiveDataClick,
      showLabel: true,
      active: liveDataOpen,
    },
    ...(showAdmin
      ? [
          {
            id: "devops",
            label: "Devops",
            icon: Wrench,
            to: "/devops",
          } satisfies BottomItem,
        ]
      : []),
    { id: "connections", label: "Connections", icon: Plug, disabled: true },
    { id: "alerts", label: "Alerts", icon: Bell, disabled: true },
    ...(showAdmin
      ? [
          {
            id: "users",
            label: "Users",
            icon: Users,
            onClick: onUsersClick,
          } satisfies BottomItem,
        ]
      : []),
  ];

  return (
    <nav
      className="flex h-full w-[var(--nav-rail-width)] shrink-0 flex-col items-center border-r border-border-subtle bg-nav-rail py-3 shadow-[1px_0_4px_rgba(0,0,0,0.04)]"
      aria-label="Main navigation"
    >
      <div className="flex flex-col items-center gap-1">
        {visibleTopics.map((topic) => {
          const Icon = topic.icon;
          return (
            <NavTooltip key={topic.id} label={topic.label}>
              <NavLink
                to={topic.path}
                className={({ isActive }) => navLinkClass(isActive, topic.showLabel)}
                aria-label={topic.label}
              >
                <Icon size={20} strokeWidth={1.75} />
                {topic.showLabel && (
                  <span
                    className="font-medium leading-none"
                    style={{ fontSize: "10px" }}
                  >
                    {topic.label}
                  </span>
                )}
              </NavLink>
            </NavTooltip>
          );
        })}
      </div>

      <div className="mt-auto flex w-full flex-col items-center gap-1 border-t border-border-subtle pt-2">
        {bottomItems.map((item) => {
          const Icon = item.icon;
          const isUsers = item.id === "users";
          const tooltipLabel = item.disabled ? `${item.label} (coming soon)` : item.label;

          if (item.to) {
            return (
              <NavTooltip key={item.id} label={tooltipLabel}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) => navLinkClass(isActive, item.showLabel)}
                  aria-label={item.label}
                >
                  <Icon size={20} strokeWidth={1.75} />
                  {item.showLabel && (
                    <span
                      className="font-medium leading-none"
                      style={{ fontSize: "10px" }}
                    >
                      {item.label}
                    </span>
                  )}
                </NavLink>
              </NavTooltip>
            );
          }

          if (item.onClick) {
            return (
              <NavTooltip key={item.id} label={tooltipLabel}>
                <button
                  type="button"
                  onClick={item.onClick}
                  className={navBtnClass(
                    (isUsers && usersOpen) || item.active,
                    item.showLabel,
                  )}
                  aria-label={item.label}
                  aria-pressed={
                    isUsers ? usersOpen : item.active ? true : undefined
                  }
                >
                  <Icon size={20} strokeWidth={1.75} />
                  {item.showLabel && (
                    <span
                      className="font-medium leading-none"
                      style={{ fontSize: "10px" }}
                    >
                      {item.label}
                    </span>
                  )}
                </button>
              </NavTooltip>
            );
          }

          return (
            <NavTooltip key={item.id} label={tooltipLabel}>
              <button
                type="button"
                className={navBtnClass(false)}
                aria-label={item.label}
                disabled
              >
                <Icon size={20} strokeWidth={1.75} className="opacity-50" />
              </button>
            </NavTooltip>
          );
        })}

        <div className="lg:hidden">
          <NavTooltip label="Toggle sidebar">
            <button
              type="button"
              onClick={onMenuClick}
              className={navBtnClass(false)}
              aria-label="Toggle sidebar"
              aria-expanded={sidebarOpen}
            >
              <span className="text-xs font-medium">≡</span>
            </button>
          </NavTooltip>
        </div>
      </div>
    </nav>
  );
}
